// 💾 src/components/trade/OrderHistory.tsx
import React, { useEffect, useMemo, useState } from "react"
import { auth } from "../../services/firebase"
import { OpenOrder } from "../../types/orders"
import "./style/OrderHistory.scss"

type UserOrders = {
  uid: string
  orders: OpenOrder[]
}

function num(v: unknown): number | null {
  if (typeof v === "number") return Number.isFinite(v) ? v : null
  if (typeof v === "string" && v.trim() !== "") {
    const n = Number(v)
    return Number.isFinite(n) ? n : null
  }
  return null
}

function getTradedByLabel(code: string): string {
  switch (code) {
    case "AM":
      return "Account Manager"
    case "AI":
      return "AI Trading Bot"
    case "PL":
      return "Platform Trading Bot"
    default:
      return code
  }
}

const PAGE_SIZE = 10

const OrderHistory: React.FC = () => {
  const user = auth.currentUser
  const [orders, setOrders] = useState<OpenOrder[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string>("")
  const [currentPage, setCurrentPage] = useState(1)

  useEffect(() => {
    const fetchOrders = async () => {
      if (!user) {
        setError("Not signed in")
        setLoading(false)
        return
      }

      try {
        const resp = await fetch("/mockOrders.json")
        if (!resp.ok) throw new Error("Failed to load mockOrders.json")

        const all: UserOrders[] = await resp.json()
        const found = all.find((u) => u.uid === user.uid)
        setOrders(found ? found.orders : [])
      } catch (err: any) {
        console.error("❌ Failed to load orders:", err)
        setError("Could not load orders")
      } finally {
        setLoading(false)
      }
    }

    fetchOrders()
  }, [user])

  const list = useMemo(() => {
    const arr = Array.isArray(orders) ? orders.slice() : []
    arr.sort((a, b) => (b.createdAt ?? 0) - (a.createdAt ?? 0))
    return arr
  }, [orders])

  const totalPages = Math.ceil(list.length / PAGE_SIZE)
  const paginated = list.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE)

  if (loading) return <p>Loading orders…</p>
  if (error) return <p className="error">{error}</p>
  if (!list.length) return <p>No past orders</p>

  return (
    <div className="order-history">
      <table>
        <thead>
          <tr>
            <th>Pair</th>
            <th>Side</th>
            <th>Qty</th>
            <th>Entry</th>
            <th>Closed Price</th>
            <th>Status</th>
            <th>P/L</th>
            <th>Traded by</th>
            <th>Time</th>
          </tr>
        </thead>
        <tbody>
          {paginated.map((o) => {
            const qty = num(o.quantity)
            const entry = num(o.tradePrice)
            const closed = num(o.priceNow)
            const pl = num(o.profitLose)
            const ts = num(o.createdAt)

            return (
              <tr key={o.id}>
                <td>{o.pair}</td>
                <td className={o.side}>{o.side.toUpperCase()}</td>
                <td>{qty !== null ? qty.toFixed(2) : "-"}</td>
                <td>{entry !== null ? entry.toFixed(4) : "-"}</td>
                <td>{closed !== null ? closed.toFixed(4) : "-"}</td>
                <td>{o.status}</td>
                <td
                  className={
                    pl !== null
                      ? pl > 0
                        ? "pl-positive"
                        : pl < 0
                        ? "pl-negative"
                        : ""
                      : ""
                  }
                >
                  {pl !== null ? `${pl.toFixed(2)} $` : "-"}
                </td>
                <td>
                  <div className="traded-by-wrapper">
                    <button className={`traded-by ${o.tradedBy}`}>
                      {o.tradedBy}
                    </button>
                    <span className="tooltip-text">
                      {getTradedByLabel(o.tradedBy)}
                    </span>
                  </div>
                </td>
                <td>{ts !== null ? new Date(ts).toLocaleString() : "-"}</td>
              </tr>
            )
          })}
        </tbody>
      </table>

      {/* 📄 Pagination Controls */}
      <div className="pagination">
        <button
          disabled={currentPage === 1}
          onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
        >
          Previous
        </button>
        <span>
          Page {currentPage} of {totalPages}
        </span>
        <button
          disabled={currentPage === totalPages}
          onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
        >
          Next
        </button>
      </div>
    </div>
  )
}

export default OrderHistory
